import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import logo from '../logo-cb-courtage.png';

const NAVY = '#1a2b4a';
const GOLD = '#c8a96e';
const LIGHT = '#f8fafc';
const BORDER = '#e5e7eb';
const API = 'https://crm.cbcourtage.fr/api';

const api = axios.create({ baseURL: API });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

/**
 * URL de téléchargement d'un document.
 *
 * Sert aux liens <a href> ouverts par un clic de l'utilisateur : c'est le
 * navigateur qui fait la requête, et il ne peut pas y placer d'en-tête
 * Authorization. L'API accepte donc le jeton en paramètre d'URL, sur les
 * seules routes /uploads/. Sans cela elle répond 401 depuis la fermeture de
 * l'API, et le client ne voit rien se passer en cliquant.
 *
 * À ne pas utiliser pour une requête émise par le code : telechargerDocuments
 * passe le jeton dans l'en-tête, qui n'a pas à se promener dans une URL.
 *
 * Équivalent de urlFichier() côté CRM (frontend/src/api.js) : toute évolution
 * se fait des deux côtés.
 */
function urlFichier(chemin) {
  const token = localStorage.getItem('token');
  if (!token) return chemin;
  return `${chemin}${chemin.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt     = (n) => n != null ? Number(n).toLocaleString('fr-FR') : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const formatPlaque = (val) => {
  const clean = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (clean.length <= 2) return clean;
  if (clean.length <= 5) return `${clean.slice(0, 2)}-${clean.slice(2)}`;
  return `${clean.slice(0, 2)}-${clean.slice(2, 5)}-${clean.slice(5, 7)}`;
};

const STATUT_COLORS = {
  'En cours':              { bg: '#dbeafe', fg: '#1d4ed8' },
  'Validé':                { bg: '#ede9fe', fg: '#7c3aed' },
  "À l'étude":            { bg: '#fef9c3', fg: '#a16207' },
  'Gagné':                 { bg: '#dcfce7', fg: '#16a34a' },
  'Gagné facturé':         { bg: '#d1fae5', fg: '#059669' },
  'Signé':                 { bg: '#cffafe', fg: '#0891b2' },
  'Perdu':                 { bg: '#fee2e2', fg: '#dc2626' },
  'Sans suite':            { bg: '#f1f5f9', fg: '#64748b' },
  'Ouvert':                { bg: '#fef9c3', fg: '#a16207' },
  "En cours d'expertise":  { bg: '#fef3c7', fg: '#d97706' },
  'Clôturé':               { bg: '#f1f5f9', fg: '#64748b' },
  'Actif':                 { bg: '#dcfce7', fg: '#16a34a' },
  'En attente':            { bg: '#f1f5f9', fg: '#64748b' },
  'Résilié':               { bg: '#fee2e2', fg: '#dc2626' },
  'Réglé':                 { bg: '#dcfce7', fg: '#16a34a' },
  'À régler':              { bg: '#ffedd5', fg: '#c2410c' },
};

// Le CRM stocke des codes ; le client, lui, doit lire du français.
// Mêmes tables que frontend/src/pages/Assurance.js côté CRM : toute
// évolution se fait des deux côtés.
const TYPE_CONTRAT_LABELS = {
  flotte:       'Flotte auto',
  rc:           'RC Professionnelle',
  rc_transport: 'RC Transport',
  batiment:     'Bâtiment',
  prevoyance:   'Prévoyance',
  mutuelle:     'Mutuelle',
  vehicule:     'Véhicule',
};

const FRACTIONNEMENT_LABELS = {
  1:  'Annuel',
  2:  'Semestriel',
  4:  'Trimestriel',
  6:  'Bimestriel',
  12: 'Mensuel',
};

const libelleContrat = (c) =>
  TYPE_CONTRAT_LABELS[c.type_contrat] || c.type_contrat || 'Contrat assurance';

const RESPONSABILITE_COLORS = {
  'Client responsable':      { bg: '#fff7ed', fg: '#c2410c', border: '#fed7aa' },
  'Tiers responsable':       { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' },
  'Responsabilité partagée': { bg: '#fdf4ff', fg: '#7e22ce', border: '#e9d5ff' },
  'Non déterminée':          { bg: '#f3f4f6', fg: '#6b7280', border: '#d1d5db' },
};

// translate="no" protège contre la traduction automatique du navigateur
function Badge({ label }) {
  const c = STATUT_COLORS[label] || { bg: '#f1f5f9', fg: '#64748b' };
  return (
    <span translate="no" style={{
      background: c.bg, color: c.fg, borderRadius: '100px',
      padding: '3px 12px', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap',
    }}>
      {label || '—'}
    </span>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: 'center', padding: '72px 24px', color: '#94a3b8' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>{icon}</div>
      <div style={{ fontSize: '15px', fontWeight: '500' }}>{text}</div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '12px', padding: '20px 24px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: `1px solid ${BORDER}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '16px', flexWrap: 'wrap',
    }}>
      {children}
    </div>
  );
}

async function downloadAccordPDF(projetId) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}/projets/${projetId}/accord-financement/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) { alert('Aucun accord disponible pour ce dossier.'); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `accord-financement-${projetId}.pdf`; a.click();
  URL.revokeObjectURL(url);
}

/**
 * Télécharge une liste de documents, un par un.
 *
 * La version précédente ouvrait un window.open par document, espacés de
 * 300 ms. Le navigateur n'en laissait passer qu'un : seul le premier part
 * dans le geste de l'utilisateur, les suivants sont vus comme des pop-ups et
 * bloqués sans le moindre message. Le bouton semblait donc marcher, et ne
 * ramenait qu'un fichier.
 *
 * On récupère chaque fichier en mémoire, puis on déclenche un
 * téléchargement local. C'est ce que fait déjà downloadAccordPDF, et c'est
 * obligatoire ici : l'attribut « download » est ignoré sur un lien pointant
 * vers un autre domaine — les fichiers sont sur crm.cbcourtage.fr, la page
 * sur www.cbcourtage.fr. Sans passer par le blob, le navigateur afficherait
 * le PDF au lieu de l'enregistrer.
 *
 * Le jeton part dans l'en-tête, et non dans l'URL : cette requête-ci est
 * émise par le code, pas par un clic sur un lien.
 */
async function telechargerDocuments(documents, dossier) {
  const token = localStorage.getItem('token');
  const echecs = [];

  for (const doc of documents) {
    try {
      const res = await fetch(`${API}/uploads/${dossier}/${doc.chemin}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { echecs.push(doc.nom_fichier); continue; }

      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.nom_fichier;
      // Firefox n'ouvre le téléchargement que si le lien est dans la page.
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Révoquer tout de suite annulerait l'enregistrement en cours.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      echecs.push(doc.nom_fichier);
    }
  }

  return echecs;
}

function Spinner() {
  return (
    <div style={{ minHeight: '100vh', background: LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: NAVY, fontWeight: '600', fontSize: '14px' }}>Chargement…</p>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function DashboardClient() {
  const navigate = useNavigate();
  const [user, setUser]                     = useState(null);
  const [entreprise, setEntreprise]         = useState(null);
  const [projets, setProjets]               = useState([]);
  const [contrats, setContrats]             = useState([]);
  const [sinistres, setSinistres]           = useState([]);
  const [conditionsParc, setConditionsParc] = useState([]);
  // Appels de prime que le cabinet a envoyés à cette entreprise, avec leur PDF.
  const [appelsPrime, setAppelsPrime]       = useState([]);
  const [searchParc, setSearchParc]         = useState('');
  const [loading, setLoading]               = useState(true);
  const [noProspect, setNoProspect]         = useState(false);
  const [activeTab, setActiveTab]           = useState('dossiers');
  const [selectedSinistre, setSelectedSinistre] = useState(null);
  const [sinistreDocuments, setSinistreDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs]           = useState(false);
  const [selectedContrat, setSelectedContrat]     = useState(null);
  const [contratDocuments, setContratDocuments]   = useState([]);
  const [loadingContratDocs, setLoadingContratDocs] = useState(false);
  const [telechargementEnCours, setTelechargementEnCours] = useState(false);

  const [showAddModal, setShowAddModal]         = useState(false);
  const [showRemoveModal, setShowRemoveModal]   = useState(false);
  const [addForm, setAddForm]                   = useState({ immat: '', date_entree: '', valeur: '', file: null });
  const [removeForm, setRemoveForm]             = useState({ immat: '', date_sortie: '', file: null });
  const [parcMessage, setParcMessage]           = useState(null);
  const [parcSubmitting, setParcSubmitting]     = useState(false);
  const [removeError, setRemoveError]           = useState('');
  // Entrées et sorties déjà déclarées, avec leur justificatif — ou son absence.
  // Sans cette liste, un client ne pouvait ni vérifier ce qu'il avait envoyé,
  // ni compléter une pièce manquante.
  const [historiqueParc, setHistoriqueParc]     = useState([]);
  const [pieceEnCours, setPieceEnCours]         = useState(null); // id du mouvement
  // Changement de mot de passe par le client lui-même. Sans lui, chaque oubli
  // ou chaque doute passait par le cabinet, qui réinitialisait à la main.
  const MDP_VIDE = { ancien: '', nouveau: '', confirmation: '' };
  const [showMdp, setShowMdp]                   = useState(false);
  const [mdpForm, setMdpForm]                   = useState(MDP_VIDE);
  const [mdpErreur, setMdpErreur]               = useState('');
  const [mdpEnvoi, setMdpEnvoi]                 = useState(false);

  const fermerMdp = () => { setShowMdp(false); setMdpForm(MDP_VIDE); setMdpErreur(''); };

  const changerMotDePasse = async () => {
    if (mdpForm.nouveau !== mdpForm.confirmation) {
      setMdpErreur('Les deux saisies du nouveau mot de passe ne correspondent pas.');
      return;
    }
    setMdpEnvoi(true);
    setMdpErreur('');
    try {
      const { data } = await api.put('/utilisateurs/me/password', {
        ancien_mot_de_passe: mdpForm.ancien,
        nouveau_mot_de_passe: mdpForm.nouveau,
      });
      // Le serveur ferme toutes les sessions du compte — c'est le but, si le
      // mot de passe a fuité — y compris celle-ci. Il renvoie donc un jeton
      // neuf : sans le ranger, le client serait déconnecté par son propre
      // changement dès la requête suivante.
      if (data?.access_token) localStorage.setItem('token', data.access_token);
      fermerMdp();
      showParcMessage('success', '✅ Mot de passe modifié. Vos autres sessions ont été fermées.');
    } catch (err) {
      // Le serveur motive ses refus : ancien mot de passe incorrect, nouveau
      // trop court, trop courant ou contenant l'email. Les afficher tels quels.
      setMdpErreur(messageServeur(err, "Le changement n'a pas pu être enregistré. Réessayez."));
    } finally {
      setMdpEnvoi(false);
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('client_user');
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem('token')) { navigate('/login'); return; }

    const load = async () => {
      try {
        // 1. Profil courant — vérifie rôle + récupère prospect_id
        const { data: me } = await api.get('/utilisateurs/me');
        if (me.role !== 'client' || me.actif === false) { logout(); return; }
        setUser(me);

        if (!me.prospect_id) { setNoProspect(true); setLoading(false); return; }

        // 2. Données via routes sécurisées /client/* (filtrées par le backend)
        const [entRes, projRes, contRes, sinRes, parcRes, histRes, appelsRes] = await Promise.all([
          api.get('/client/entreprise'),
          api.get('/client/projets'),
          api.get('/client/contrats'),
          api.get('/client/sinistres'),
          api.get('/client/conditions-parc').catch(() => ({ data: [] })),
          api.get('/client/parc/historique').catch(() => ({ data: [] })),
          api.get('/client/appels-prime').catch(() => ({ data: [] })),
        ]);

        setEntreprise(entRes.data);
        setProjets(projRes.data);
        setContrats(contRes.data);
        setSinistres(sinRes.data);
        setConditionsParc(parcRes.data);
        setHistoriqueParc(histRes.data);
        setAppelsPrime(appelsRes.data);
      } catch {
        logout();
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate, logout]);

  const refreshParc = useCallback(async () => {
    try {
      const { data } = await api.get('/client/conditions-parc');
      setConditionsParc(data);
    } catch {}
    try {
      const { data } = await api.get('/client/parc/historique');
      setHistoriqueParc(data);
    } catch {}
  }, []);

  // Joint le justificatif d'un mouvement déjà déclaré. Le serveur refuse de
  // remplacer une pièce existante : un justificatif est une preuve.
  const joindrePiece = async (mouvement, fichier) => {
    if (!fichier) return;
    setPieceEnCours(mouvement.id);
    try {
      const fd = new FormData();
      fd.append('document', fichier);
      await api.post(`/client/parc/historique/${mouvement.id}/piece`, fd);
      await refreshParc();
      showParcMessage('success', `✅ Justificatif joint pour ${mouvement.immatriculation}`);
    } catch (err) {
      showParcMessage('error', `❌ ${messageServeur(err, "Le justificatif n'a pas pu être envoyé.")}`);
    } finally {
      setPieceEnCours(null);
    }
  };

  if (loading) return <Spinner />;

  const tabs = [
    { key: 'dossiers',  label: '📁 Mes dossiers',       count: projets.length },
    { key: 'contrats',  label: '🛡️ Mes contrats',       count: contrats.length },
    { key: 'appels',    label: '💶 Mes appels de prime', count: appelsPrime.length },
    { key: 'parc',      label: '🚗 Mon état de parc',   count: conditionsParc.length },
    { key: 'sinistres', label: '⚠️ Mes sinistres',       count: sinistres.length },
  ];

  const nomEntreprise = entreprise?.nom_societe || '';

  const openSinistreModal = async (s) => {
    setSelectedSinistre(s);
    setSinistreDocuments([]);
    setLoadingDocs(true);
    try {
      const { data } = await api.get(`/sinistres/${s.id}/documents`);
      setSinistreDocuments(data);
    } catch {
      setSinistreDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const closeSinistreModal = () => {
    setSelectedSinistre(null);
    setSinistreDocuments([]);
  };

  const openContratModal = async (c) => {
    setSelectedContrat(c);
    setContratDocuments([]);
    setLoadingContratDocs(true);
    try {
      const { data } = await api.get(`/contrats-assurance/${c.id}/documents`);
      setContratDocuments(data);
    } catch {
      setContratDocuments([]);
    } finally {
      setLoadingContratDocs(false);
    }
  };

  const closeContratModal = () => {
    setSelectedContrat(null);
    setContratDocuments([]);
  };

  // Les fichiers partent un par un : le bouton reste désactivé le temps que
  // la série se termine, sinon un second clic relance tout par-dessus.
  const toutTelecharger = async (documents, dossier) => {
    setTelechargementEnCours(true);
    try {
      const echecs = await telechargerDocuments(documents, dossier);
      if (echecs.length) {
        alert(
          echecs.length === documents.length
            ? "Le téléchargement n'a pas abouti. Réessayez, ou contactez CB Courtage."
            : `Ces documents n'ont pas pu être téléchargés :\n\n${echecs.join('\n')}`
        );
      }
    } finally {
      setTelechargementEnCours(false);
    }
  };

  // Le serveur dit précisément ce qui ne va pas (justificatif manquant, date
  // illisible…). Un message unique pour toutes les causes laissait chercher au
  // hasard.
  const messageServeur = (err, parDefaut) => {
    const detail = err?.response?.data?.detail;
    return typeof detail === 'string' ? detail : parDefaut;
  };

  const showParcMessage = (type, text) => {
    setParcMessage({ type, text });
    setTimeout(() => setParcMessage(null), 4000);
  };

  const handleAddVehicle = async () => {
    if (!addForm.immat || !addForm.date_entree) return;
    setParcSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('immatriculation', addForm.immat);
      fd.append('date_entree', addForm.date_entree);
      if (addForm.valeur) fd.append('valeur_declaree', addForm.valeur);
      if (addForm.file) fd.append('document', addForm.file);
      await api.post('/client/parc/entree', fd);
      setShowAddModal(false);
      setAddForm({ immat: '', date_entree: '', valeur: '', file: null });
      await refreshParc();
      showParcMessage('success', '✅ Véhicule ajouté');
    } catch (err) {
      showParcMessage('error', `❌ ${messageServeur(err, "Erreur lors de l'ajout. Veuillez réessayer.")}`);
    } finally {
      setParcSubmitting(false);
    }
  };

  const handleRemoveVehicle = async () => {
    if (!removeForm.immat || !removeForm.date_sortie || !removeForm.file) return;
    setParcSubmitting(true);
    setRemoveError('');
    try {
      const fd = new FormData();
      fd.append('immatriculation', removeForm.immat);
      fd.append('date_sortie', removeForm.date_sortie);
      if (removeForm.file) fd.append('document', removeForm.file);
      await api.post('/client/parc/sortie', fd);
      setShowRemoveModal(false);
      setRemoveForm({ immat: '', date_sortie: '', file: null });
      await refreshParc();
      showParcMessage('success', '✅ Véhicule retiré');
    } catch (err) {
      if (err.response?.status === 404) {
        setRemoveError('❌ Immatriculation introuvable dans votre parc.');
      } else if (err.response?.status === 400) {
        // Refus motivé (justificatif, date) : on reste dans la fenêtre pour
        // corriger, au lieu de la fermer et de perdre la saisie.
        setRemoveError(`❌ ${messageServeur(err, 'Saisie incomplète.')}`);
        return;
      } else {
        showParcMessage('error', `❌ ${messageServeur(err, 'Erreur lors du retrait. Veuillez réessayer.')}`);
        setShowRemoveModal(false);
        setRemoveForm({ immat: '', date_sortie: '', file: null });
      }
    } finally {
      setParcSubmitting(false);
    }
  };

  const exportParc = () => {
    const boolXls = (v) => v === true ? 'Oui' : v === false ? 'Non' : '';
    const strXls  = (v) => v != null ? String(v) : '';
    const dtXls   = (v) => v ? new Date(v + 'T00:00:00').toLocaleDateString('fr-FR') : '';

    const rows = conditionsParc.map(c => ({
      'Immatriculation':           strXls(c.immatriculation),
      'Marque / Modèle':           strXls(c.marque_modele),
      'Genre':                     strXls(c.genre),
      'Mise en circulation':       dtXls(c.date_mise_en_circulation),
      'Énergie':                   strXls(c.energie),
      'Usage':                     strXls(c.usage),
      'Zone':                      strXls(c.zone),
      'RC Déf./Rec.':              boolXls(c.rc_defense_recours),
      'RC Fonctionnement':         boolXls(c.rc_fonctionnement),
      'Faute inexcusable':         boolXls(c.faute_inexcusable),
      'DTA':                       boolXls(c.dommage_tous_accidents),
      'Franchise DTA':             strXls(c.dommage_franchise),
      'Vol':                       boolXls(c.vol),
      'Franchise Vol':             strXls(c.vol_franchise),
      'Incendie':                  boolXls(c.incendie),
      'Franchise Incendie':        strXls(c.incendie_franchise),
      'Cat. Nat.':                 boolXls(c.catastrophes_naturelles),
      'Remorquage / Levage':       boolXls(c.remorquage_levage),
      'Limitation remorquage':     strXls(c.remorquage_limitation),
      'Bris de glace':             boolXls(c.bris_de_glace),
      'Franchise BDG':             strXls(c.bris_franchise),
      'Immobilisation':            boolXls(c.immobilisation),
      'Sécurité conducteur':       boolXls(c.securite_conducteur),
      'Perte financière':          boolXls(c.perte_financiere),
      'Assistance PL':             boolXls(c.assistance_chauffeur_pl),
      'Assistance ch.+veh.':       boolXls(c.assistance_chauffeur_et_vehicule),
      'Véhicule remplacement':     boolXls(c.vehicule_remplacement),
      'Date entrée':               dtXls(c.date_entree),
      'Date sortie':               dtXls(c.date_sortie),
      'Prime HT':                  strXls(c.prime_ht),
      'Prime TTC':                 strXls(c.prime_ttc),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'État de parc');

    const dateStr = new Date().toISOString().slice(0, 10);
    const safeName = (nomEntreprise || 'entreprise').replace(/[^a-zA-Z0-9À-ɏ\s-]/g, '').trim().replace(/\s+/g, '_');
    XLSX.writeFile(wb, `etat_de_parc_${safeName}_${dateStr}.xlsx`);
  };

  return (
    // lang="fr" empêche le navigateur de proposer/appliquer une traduction automatique
    <div lang="fr" style={{ minHeight: '100vh', background: LIGHT, fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* ── Header ── */}
      <header style={{ background: NAVY, padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <img src={logo} alt="CB Courtage" style={{ height: '38px', borderRadius: '50%', objectFit: 'cover' }} />
          <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '18px', fontWeight: '200' }}>|</span>
          <span translate="no" style={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px', fontWeight: '500' }}>Espace client</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {user && (
            <span translate="no" style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>
              {user.nom}
            </span>
          )}
          <button onClick={() => setShowMdp(true)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'rgba(255,255,255,0.85)', padding: '7px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
            Mot de passe
          </button>
          <button onClick={logout} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#fff', padding: '7px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
            Déconnexion
          </button>
        </div>
      </header>

      <main style={{ maxWidth: '1060px', margin: '0 auto', padding: '40px 24px' }}>

        {/* ── Bienvenue ── */}
        <div style={{ marginBottom: '32px' }}>
          <h1 translate="no" style={{ fontSize: '24px', fontWeight: '800', color: NAVY, margin: '0 0 6px' }}>
            {nomEntreprise ? `Bonjour, ${nomEntreprise} 👋` : 'Bonjour 👋'}
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            Voici un récapitulatif de vos dossiers, contrats et sinistres.
          </p>
          {noProspect && (
            <div style={{ marginTop: '16px', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '10px', padding: '14px 18px', fontSize: '14px', color: '#92400e' }}>
              ⚠️ Votre compte n'est pas encore lié à une entreprise. Contactez votre conseiller CB Courtage.
            </div>
          )}
        </div>

        {/* ── Contacts CB Courtage ── */}
        <div style={{ background: '#fff', borderRadius: '12px', border: `1px solid ${BORDER}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '16px 24px', marginBottom: '28px', display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', fontWeight: '700', color: NAVY, marginRight: '4px', whiteSpace: 'nowrap' }}>📞 Contacts CB Courtage</span>
          <span style={{ width: '1px', background: BORDER, alignSelf: 'stretch', flexShrink: 0 }} />
          {[
            { icon: '📞', label: '03 20 41 60 10', href: 'tel:+33320416010' },
            { icon: '✉️', label: 'gestion@cbcourtage.fr', href: 'mailto:gestion@cbcourtage.fr' },
            { icon: '📍', label: 'Templemars, France', href: null },
          ].map(({ icon, label, href }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151' }}>
              <span style={{ fontSize: '15px' }}>{icon}</span>
              {href ? (
                <a href={href} style={{ color: '#374151', textDecoration: 'none', fontWeight: '500' }}
                  onMouseEnter={e => e.currentTarget.style.color = GOLD}
                  onMouseLeave={e => e.currentTarget.style.color = '#374151'}>
                  {label}
                </a>
              ) : (
                <span style={{ fontWeight: '500' }}>{label}</span>
              )}
            </div>
          ))}
        </div>

        {/* ── KPIs ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))', gap: '14px', marginBottom: '36px' }}>
          {[
            { label: 'Dossiers',  val: projets.length,   color: '#0891b2', icon: '📁' },
            { label: 'Contrats',  val: contrats.length,  color: '#16a34a', icon: '🛡️' },
            { label: 'Véhicules', val: conditionsParc.length, color: NAVY, icon: '🚗' },
            { label: 'Sinistres', val: sinistres.length, color: '#d97706', icon: '⚠️' },
          ].map(k => (
            <div key={k.label} style={{ background: '#fff', borderRadius: '12px', padding: '18px 20px', border: `1px solid ${BORDER}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: `4px solid ${k.color}` }}>
              <div style={{ fontSize: '26px', fontWeight: '800', color: k.color }}>{k.val}</div>
              <div translate="no" style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', fontWeight: '500' }}>{k.icon} {k.label}</div>
            </div>
          ))}
        </div>

        {/* ── Onglets ── */}
        <div style={{ display: 'flex', gap: '2px', borderBottom: `2px solid ${BORDER}`, marginBottom: '28px', overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} translate="no" style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '10px 18px', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap',
              color: activeTab === t.key ? NAVY : '#94a3b8',
              borderBottom: activeTab === t.key ? `2px solid ${GOLD}` : '2px solid transparent',
              marginBottom: '-2px', transition: 'color 0.15s',
            }}>
              {t.label}
              {t.count > 0 && (
                <span style={{ marginLeft: '6px', background: activeTab === t.key ? NAVY : '#e2e8f0', color: activeTab === t.key ? '#fff' : '#64748b', borderRadius: '99px', fontSize: '11px', fontWeight: '700', padding: '1px 7px' }}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Dossiers de financement ── */}
        {activeTab === 'dossiers' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {projets.length === 0 ? (
              <EmptyState icon="📁" text="Aucun dossier de financement pour le moment." />
            ) : projets.map(p => (
              <Card key={p.id}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div translate="no" style={{ fontWeight: '700', color: NAVY, fontSize: '15px', marginBottom: '5px' }}>
                    {p.nom_produit || p.materiel || p.type_financement || 'Dossier de financement'}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '13px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {p.montant_financement != null && <span>💶 {fmt(p.montant_financement)} €</span>}
                    {p.duree_financement   && <span>⏱ {p.duree_financement} mois</span>}
                    {p.organisme_financier && <span translate="no">🏦 {p.organisme_financier}</span>}
                    <span>📅 {fmtDate(p.date_creation)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                  <Badge label={p.statut_projet || 'En cours'} />
                  {['Gagné', 'Signé', 'Gagné facturé'].includes(p.statut_projet) && (
                    <button onClick={() => downloadAccordPDF(p.id)} style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '7px', color: '#0369a1', padding: '6px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      ⬇ PDF
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ── Contrats assurance ── */}
        {activeTab === 'contrats' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {contrats.length === 0 ? (
              <EmptyState icon="🛡️" text="Aucun contrat assurance pour le moment." />
            ) : contrats.map(c => (
              <div key={c.id} onClick={() => openContratModal(c)} style={{ cursor: 'pointer' }}>
                <Card>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontWeight: '700', color: NAVY, fontSize: '15px', marginBottom: '5px' }}>
                      <span translate="no">{libelleContrat(c)}</span>
                      {c.num_contrat && <span style={{ fontWeight: '400', color: '#64748b', fontSize: '13px' }}> — N° <span translate="no">{c.num_contrat}</span></span>}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '13px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {c.date_debut    && <span>📅 Début {fmtDate(c.date_debut)}</span>}
                      {c.date_echeance && <span>⏳ Échéance {fmtDate(c.date_echeance)}</span>}
                      {c.prime_annuelle != null && <span>💶 {fmt(c.prime_annuelle)} €/an</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <Badge label={c.statut || 'Actif'} />
                    <span style={{ color: '#cbd5e1', fontSize: '16px' }}>›</span>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        )}

        {/* ── Appels de prime ── */}
        {activeTab === 'appels' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {appelsPrime.length === 0 ? (
              <EmptyState icon="💶" text="Aucun appel de prime pour le moment. Ceux que CB Courtage vous adresse apparaissent ici." />
            ) : appelsPrime.map(a => (
              <Card key={a.id}>
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <div style={{ fontWeight: '700', color: NAVY, fontSize: '15px', marginBottom: '5px' }}>
                    Appel de prime <span translate="no">n° {a.numero_assureur}</span>
                  </div>
                  <div style={{ color: '#64748b', fontSize: '13px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {a.periode_debut && <span>📅 Du {fmtDate(a.periode_debut)} au {fmtDate(a.periode_fin)}</span>}
                    <span>🛡️ {TYPE_CONTRAT_LABELS[a.type_contrat] || a.type_contrat || 'Contrat'}{a.compagnie ? ` · ${a.compagnie}` : ''}{a.num_contrat ? ` — N° ${a.num_contrat}` : ''}</span>
                    {a.date_envoi && <span>✉️ Reçu le {fmtDate(a.date_envoi)}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: '800', color: NAVY, fontSize: '17px' }}>{fmt(a.montant)} €</div>
                  <Badge label={a.statut === 'regle' ? 'Réglé' : 'À régler'} />
                  {a.documents.map(d => (
                    // Lien <a href> : le navigateur ne peut pas poser d'en-tête
                    // Authorization, le jeton passe donc en paramètre d'URL
                    // (urlFichier) — seulement sur les routes de téléchargement.
                    <a key={d.id} href={urlFichier(`${API}/client/uploads/appels-prime/${d.id}`)}
                       target="_blank" rel="noopener noreferrer"
                       style={{ background: '#f0f7ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '7px 12px', fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}>
                      📎 Télécharger
                    </a>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ── État de parc ── */}
        {activeTab === 'parc' && (() => {
          const query = searchParc.replace(/-/g, '').toUpperCase();
          const filtered = conditionsParc.filter(c =>
            !query || (c.immatriculation || '').replace(/-/g, '').toUpperCase().includes(query)
          );
          return (
            <div>
              {/* Toolbar */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={searchParc}
                  onChange={e => setSearchParc(formatPlaque(e.target.value))}
                  placeholder="Rechercher une plaque (ex: AB-123-CD)"
                  maxLength={9}
                  style={{
                    flex: 1, minWidth: '160px', boxSizing: 'border-box',
                    padding: '10px 16px', borderRadius: '8px',
                    border: `1.5px solid ${searchParc ? GOLD : BORDER}`,
                    fontSize: '14px', fontFamily: 'monospace', letterSpacing: '1px',
                    outline: 'none', color: NAVY, background: '#fff',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={e => e.target.style.borderColor = GOLD}
                  onBlur={e => e.target.style.borderColor = searchParc ? GOLD : BORDER}
                />
                <button
                  onClick={exportParc}
                  style={{ flexShrink: 0, background: NAVY, color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', transition: 'opacity 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  📥 Exporter en Excel
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  style={{ flexShrink: 0, background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', transition: 'opacity 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  ➕ Ajouter un véhicule
                </button>
                <button
                  onClick={() => { setShowRemoveModal(true); setRemoveError(''); }}
                  style={{ flexShrink: 0, background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', transition: 'opacity 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  ➖ Retirer un véhicule
                </button>
              </div>

              {/* Message succès / erreur */}
              {parcMessage && (
                <div style={{
                  marginBottom: '12px', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                  background: parcMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
                  color: parcMessage.type === 'success' ? '#16a34a' : '#dc2626',
                  border: `1px solid ${parcMessage.type === 'success' ? '#86efac' : '#fca5a5'}`,
                }}>
                  {parcMessage.text}
                </div>
              )}

              {conditionsParc.length === 0 ? (
                <EmptyState icon="🚗" text="Aucune condition de parc disponible pour le moment." />
              ) : (
                <>
                  {searchParc && (
                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#64748b' }}>
                      {filtered.length} véhicule{filtered.length !== 1 ? 's' : ''} trouvé{filtered.length !== 1 ? 's' : ''}
                      <button onClick={() => setSearchParc('')} style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '12px', padding: 0 }}>
                        ✕ Effacer
                      </button>
                    </div>
                  )}
                  <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '500px', background: '#fff', borderRadius: '12px', border: `1px solid ${BORDER}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '1400px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 1 }}>
                          {[
                            'Immat.', 'Marque / Modèle', 'Genre', 'MEC', 'Énergie', 'Usage', 'Zone',
                            'RC Déf./Rec.', 'RC Fonct.', 'Faute inex.', 'DTA', 'Fr. DTA',
                            'Vol', 'Fr. Vol', 'Incendie', 'Fr. Inc.', 'Cat. Nat.',
                            'Remor./Lev.', 'Lim.', 'Bris glace', 'Fr. BDG',
                            'Immob.', 'Sécu. cond.', 'Perte fin.',
                            'Assist. PL', 'Assist. ch.+veh.', 'Veh. rempl.',
                            'Entrée', 'Sortie', 'Prime HT', 'Prime TTC',
                          ].map((h, i) => (
                            <th key={i} style={{ padding: '10px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((c, idx) => {
                          const bool = (v) => v === true ? '✅' : v === false ? '❌' : <span style={{ color: '#cbd5e1' }}>—</span>;
                          const str  = (v) => v || <span style={{ color: '#cbd5e1' }}>—</span>;
                          const dt   = (v) => v ? new Date(v + 'T00:00:00').toLocaleDateString('fr-FR') : <span style={{ color: '#cbd5e1' }}>—</span>;
                          return (
                            <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                              <td style={{ padding: '10px 10px', fontWeight: '700', color: NAVY, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '13px' }}>{str(c.immatriculation)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap', fontWeight: '500', color: '#374151' }}>{str(c.marque_modele)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>{str(c.genre)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>{dt(c.date_mise_en_circulation)}</td>
                              <td style={{ padding: '10px 10px' }}>{str(c.energie)}</td>
                              <td style={{ padding: '10px 10px' }}>{str(c.usage)}</td>
                              <td style={{ padding: '10px 10px' }}>{str(c.zone)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.rc_defense_recours)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.rc_fonctionnement)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.faute_inexcusable)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.dommage_tous_accidents)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap', color: '#64748b' }}>{str(c.dommage_franchise)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.vol)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap', color: '#64748b' }}>{str(c.vol_franchise)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.incendie)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap', color: '#64748b' }}>{str(c.incendie_franchise)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.catastrophes_naturelles)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.remorquage_levage)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap', color: '#64748b' }}>{str(c.remorquage_limitation)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.bris_de_glace)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap', color: '#64748b' }}>{str(c.bris_franchise)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.immobilisation)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.securite_conducteur)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.perte_financiere)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.assistance_chauffeur_pl)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.assistance_chauffeur_et_vehicule)}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'center' }}>{bool(c.vehicule_remplacement)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>{dt(c.date_entree)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>{dt(c.date_sortie)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>{str(c.prime_ht)}</td>
                              <td style={{ padding: '10px 10px', whiteSpace: 'nowrap', fontWeight: '700', color: NAVY }}>{str(c.prime_ttc)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {filtered.length === 0 && searchParc && (
                    <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '14px' }}>
                      Aucun véhicule trouvé pour « {searchParc} »
                    </div>
                  )}
                </>
              )}

              {/* Historique des entrées et sorties déclarées. Un justificatif
                  manquant y est signalé, avec de quoi le déposer : c'est le
                  seul chemin par lequel une pièce oubliée — ou perdue — peut
                  encore parvenir au cabinet. */}
              {historiqueParc.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: NAVY, marginBottom: '4px' }}>
                    Entrées et sorties déclarées
                  </div>
                  {historiqueParc.some(h => !h.nom_fichier) && (
                    <div style={{ fontSize: '12.5px', color: '#b45309', marginBottom: '10px' }}>
                      ⚠️ {historiqueParc.filter(h => !h.nom_fichier).length} justificatif(s) manquant(s) —
                      merci de les joindre pour que votre dossier soit complet.
                    </div>
                  )}
                  <div style={{ background: '#fff', borderRadius: '12px', border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
                    {historiqueParc.map((h, i) => (
                      <div key={h.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                        flexWrap: 'wrap', padding: '12px 16px',
                        borderTop: i === 0 ? 'none' : `1px solid ${BORDER}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                          <span style={{
                            fontSize: '11px', fontWeight: '700', padding: '2px 9px', borderRadius: '99px',
                            background: h.type_action === 'entree' ? '#dcfce7' : '#fee2e2',
                            color: h.type_action === 'entree' ? '#16a34a' : '#dc2626',
                          }}>
                            {h.type_action === 'entree' ? 'Entrée' : 'Sortie'}
                          </span>
                          <span style={{ fontFamily: 'monospace', fontWeight: '700', color: NAVY, letterSpacing: '1px' }}>
                            {h.immatriculation}
                          </span>
                          <span style={{ fontSize: '12.5px', color: '#64748b' }}>{fmtDate(h.date_action)}</span>
                        </div>
                        {h.nom_fichier ? (
                          <span style={{ fontSize: '12.5px', color: '#16a34a' }}>📎 {h.nom_fichier}</span>
                        ) : (
                          <label style={{
                            fontSize: '12.5px', fontWeight: '600', color: '#fff', background: '#b45309',
                            padding: '6px 12px', borderRadius: '8px',
                            cursor: pieceEnCours === h.id ? 'wait' : 'pointer',
                            opacity: pieceEnCours === h.id ? 0.6 : 1,
                          }}>
                            {pieceEnCours === h.id ? 'Envoi…' : 'Justificatif manquant — joindre'}
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                                   disabled={pieceEnCours === h.id}
                                   onChange={e => { joindrePiece(h, e.target.files[0]); e.target.value = ''; }}
                                   style={{ display: 'none' }} />
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Sinistres ── */}
        {activeTab === 'sinistres' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {sinistres.length === 0 ? (
              <EmptyState icon="⚠️" text="Aucun sinistre déclaré." />
            ) : sinistres.map(s => (
              <div key={s.id} onClick={() => openSinistreModal(s)} style={{ cursor: 'pointer' }}>
                <Card>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontWeight: '700', color: NAVY, fontSize: '15px', marginBottom: '5px' }}>
                      <span translate="no">{s.categorie || s.type_vehicule || 'Sinistre'}</span>
                      {s.plaque_immatriculation && (
                        <span translate="no" style={{ marginLeft: '8px', background: '#f1f5f9', border: `1px solid ${BORDER}`, borderRadius: '5px', padding: '1px 8px', fontFamily: 'monospace', fontSize: '12px', color: '#374151', fontWeight: '600' }}>
                          {s.plaque_immatriculation}
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '13px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {s.numero && <span translate="no">N° {s.numero}</span>}
                      {s.date_sinistre && <span>📅 {fmtDate(s.date_sinistre)}</span>}
                      {s.num_sinistre_assurance && <span translate="no">Réf. : {s.num_sinistre_assurance}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <Badge label={s.statut || 'Ouvert'} />
                    <span style={{ color: '#cbd5e1', fontSize: '16px' }}>›</span>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* ── Modal sinistre ── */}
      {selectedSinistre && (() => {
        const s = selectedSinistre;
        const Row = ({ label, value }) => value ? (
          <div style={{ display: 'flex', gap: '12px', padding: '9px 0', borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ minWidth: '180px', fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>{label}</span>
            <span translate="no" style={{ fontSize: '13px', color: '#1e293b', fontWeight: '500' }}>{value}</span>
          </div>
        ) : null;
        return (
          <div
            onClick={closeSinistreModal}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            >
              {/* En-tête modal */}
              <div style={{ background: NAVY, borderRadius: '16px 16px 0 0', padding: '20px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Détail du sinistre</div>
                  <div translate="no" style={{ color: '#fff', fontSize: '17px', fontWeight: '800' }}>
                    {s.categorie || s.type_vehicule || 'Sinistre'}
                    {s.plaque_immatriculation && (
                      <span style={{ marginLeft: '10px', background: 'rgba(255,255,255,0.12)', borderRadius: '5px', padding: '2px 8px', fontFamily: 'monospace', fontSize: '13px', fontWeight: '600' }}>
                        {s.plaque_immatriculation}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={closeSinistreModal}
                  style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '18px', lineHeight: 1, cursor: 'pointer', padding: '6px 10px', flexShrink: 0 }}
                >
                  ✕
                </button>
              </div>

              {/* Corps modal */}
              <div style={{ padding: '20px 24px' }}>
                {/* Statut */}
                <div style={{ marginBottom: '16px' }}>
                  <Badge label={s.statut || 'Ouvert'} />
                </div>

                {/* Identification */}
                <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: GOLD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Identification</div>
                <Row label="Numéro CB Courtage"  value={s.numero} />
                <Row label="Réf. assurance"      value={s.num_sinistre_assurance} />
                <Row label="N° contrat"          value={s.num_contrat_assurance} />
                <Row label="Plaque"              value={s.plaque_immatriculation} />
                <Row label="Type de véhicule"    value={s.type_vehicule} />
                <Row label="Catégorie"           value={s.categorie} />

                {/* Dates */}
                <div style={{ marginTop: '16px', marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: GOLD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dates</div>
                <Row label="Date du sinistre"    value={fmtDate(s.date_sinistre)} />
                <Row label="Ordre de mission"    value={fmtDate(s.date_ordre_mission)} />
                <Row label="Rapport d'expertise" value={fmtDate(s.date_rapport_expertise)} />
                <Row label="Date de clôture"     value={fmtDate(s.date_cloture)} />

                {/* Commentaire clôture */}
                {s.commentaire_cloture && (
                  <>
                    <div style={{ marginTop: '16px', marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: GOLD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Commentaire de clôture</div>
                    <p style={{ margin: 0, fontSize: '13px', color: '#374151', background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', lineHeight: '1.6' }}>{s.commentaire_cloture}</p>
                  </>
                )}

                {/* Règlement (montant final + responsabilité) */}
                {(s.statut === 'Clos' && s.montant_final != null) || s.responsabilite ? (
                  <>
                    <div style={{ marginTop: '16px', marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: GOLD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Règlement</div>
                    {s.statut === 'Clos' && s.montant_final != null && (
                      <div style={{ display: 'flex', gap: '12px', padding: '9px 0', borderBottom: `1px solid ${BORDER}` }}>
                        <span style={{ minWidth: '180px', fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>💰 Montant final</span>
                        <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: '700' }}>
                          {Number(s.montant_final).toLocaleString('fr-FR')} €
                        </span>
                      </div>
                    )}
                    {s.responsabilite && (() => {
                      const rc = RESPONSABILITE_COLORS[s.responsabilite] || { bg: '#f3f4f6', fg: '#6b7280', border: '#d1d5db' };
                      return (
                        <div style={{ display: 'flex', gap: '12px', padding: '9px 0', borderBottom: `1px solid ${BORDER}`, alignItems: 'center' }}>
                          <span style={{ minWidth: '180px', fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>Responsabilité</span>
                          <span translate="no" style={{ background: rc.bg, color: rc.fg, border: `1px solid ${rc.border}`, padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '700' }}>
                            {s.responsabilite}
                          </span>
                        </div>
                      );
                    })()}
                  </>
                ) : null}

                {/* Pièces jointes */}
                <div style={{ marginTop: '16px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: GOLD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pièces jointes</span>
                  {!loadingDocs && sinistreDocuments.length > 0 && (
                    <button
                      onClick={() => toutTelecharger(sinistreDocuments, 'sinistres')}
                      disabled={telechargementEnCours}
                      style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', cursor: telechargementEnCours ? 'default' : 'pointer', opacity: telechargementEnCours ? 0.6 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      {telechargementEnCours ? '⏳ Téléchargement…' : '📥 Tout télécharger'}
                    </button>
                  )}
                </div>
                {loadingDocs ? (
                  <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Chargement…</p>
                ) : sinistreDocuments.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Aucune pièce jointe.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {sinistreDocuments.map(doc => (
                      <a
                        key={doc.id}
                        href={urlFichier(`${API}/uploads/sinistres/${doc.chemin}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: '8px', textDecoration: 'none', color: NAVY, fontSize: '13px', fontWeight: '500' }}
                      >
                        <span style={{ fontSize: '16px' }}>📄</span>
                        <span translate="no" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nom_fichier}</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8', flexShrink: 0 }}>⬇ Télécharger</span>
                      </a>
                    ))}
                  </div>
                )}

                {/* Bouton fermer */}
                <button
                  onClick={closeSinistreModal}
                  style={{ marginTop: '24px', width: '100%', background: NAVY, color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal contrat ── */}
      {selectedContrat && (() => {
        const c = selectedContrat;
        const Row = ({ label, value }) => value ? (
          <div style={{ display: 'flex', gap: '12px', padding: '9px 0', borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ minWidth: '180px', fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>{label}</span>
            <span translate="no" style={{ fontSize: '13px', color: '#1e293b', fontWeight: '500' }}>{value}</span>
          </div>
        ) : null;
        return (
          <div
            onClick={closeContratModal}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            >
              {/* En-tête modal */}
              <div style={{ background: NAVY, borderRadius: '16px 16px 0 0', padding: '20px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Détail du contrat</div>
                  <div translate="no" style={{ color: '#fff', fontSize: '17px', fontWeight: '800' }}>
                    {libelleContrat(c)}
                    {c.num_contrat && (
                      <span style={{ marginLeft: '10px', background: 'rgba(255,255,255,0.12)', borderRadius: '5px', padding: '2px 8px', fontFamily: 'monospace', fontSize: '13px', fontWeight: '600' }}>
                        {c.num_contrat}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={closeContratModal}
                  style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '18px', lineHeight: 1, cursor: 'pointer', padding: '6px 10px', flexShrink: 0 }}
                >
                  ✕
                </button>
              </div>

              {/* Corps modal */}
              <div style={{ padding: '20px 24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <Badge label={c.statut || 'Actif'} />
                </div>

                <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: GOLD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Identification</div>
                <Row label="Numéro CB Courtage" value={c.numero} />
                <Row label="N° de contrat"      value={c.num_contrat} />
                <Row label="Type de contrat"    value={TYPE_CONTRAT_LABELS[c.type_contrat] || c.type_contrat} />
                <Row label="Compagnie"          value={c.compagnie} />

                <div style={{ marginTop: '16px', marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: GOLD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Période</div>
                <Row label="Date de début" value={fmtDate(c.date_debut)} />
                <Row label="Échéance"      value={fmtDate(c.date_echeance)} />

                {(c.prime_annuelle != null || c.fractionnement_paiement) && (
                  <>
                    <div style={{ marginTop: '16px', marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: GOLD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cotisation</div>
                    {c.prime_annuelle != null && (
                      <div style={{ display: 'flex', gap: '12px', padding: '9px 0', borderBottom: `1px solid ${BORDER}` }}>
                        <span style={{ minWidth: '180px', fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>💶 Prime annuelle</span>
                        <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: '700' }}>{fmt(c.prime_annuelle)} €</span>
                      </div>
                    )}
                    <Row
                      label="Fractionnement"
                      value={FRACTIONNEMENT_LABELS[c.fractionnement_paiement] || (c.fractionnement_paiement ? `${c.fractionnement_paiement}x/an` : null)}
                    />
                  </>
                )}

                {/* Pièces jointes */}
                <div style={{ marginTop: '16px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: GOLD, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pièces jointes</span>
                  {!loadingContratDocs && contratDocuments.length > 0 && (
                    <button
                      onClick={() => toutTelecharger(contratDocuments, 'contrats')}
                      disabled={telechargementEnCours}
                      style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', cursor: telechargementEnCours ? 'default' : 'pointer', opacity: telechargementEnCours ? 0.6 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      {telechargementEnCours ? '⏳ Téléchargement…' : '📥 Tout télécharger'}
                    </button>
                  )}
                </div>
                {loadingContratDocs ? (
                  <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Chargement…</p>
                ) : contratDocuments.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Aucune pièce jointe.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {contratDocuments.map(doc => (
                      <a
                        key={doc.id}
                        href={urlFichier(`${API}/uploads/contrats/${doc.chemin}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: '8px', textDecoration: 'none', color: NAVY, fontSize: '13px', fontWeight: '500' }}
                      >
                        <span style={{ fontSize: '16px' }}>📄</span>
                        <span translate="no" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nom_fichier}</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8', flexShrink: 0 }}>⬇ Télécharger</span>
                      </a>
                    ))}
                  </div>
                )}

                <button
                  onClick={closeContratModal}
                  style={{ marginTop: '24px', width: '100%', background: NAVY, color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal ajouter un véhicule ── */}
      {showAddModal && (
        <div
          onClick={() => { setShowAddModal(false); setAddForm({ immat: '', date_entree: '', valeur: '', file: null }); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ background: '#16a34a', borderRadius: '16px 16px 0 0', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ color: '#fff', fontSize: '16px', fontWeight: '800' }}>➕ Ajouter un véhicule</div>
              <button
                onClick={() => { setShowAddModal(false); setAddForm({ immat: '', date_entree: '', valeur: '', file: null }); }}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '18px', lineHeight: 1, cursor: 'pointer', padding: '5px 10px' }}
              >✕</button>
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Immatriculation *</label>
                <input
                  type="text"
                  value={addForm.immat}
                  onChange={e => setAddForm(f => ({ ...f, immat: formatPlaque(e.target.value) }))}
                  placeholder="AB-123-CD"
                  maxLength={9}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '8px', border: `1.5px solid ${BORDER}`, fontSize: '16px', fontFamily: 'monospace', letterSpacing: '2px', color: NAVY, outline: 'none', fontWeight: '700' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Date d'entrée *</label>
                <input
                  type="date"
                  value={addForm.date_entree}
                  onChange={e => setAddForm(f => ({ ...f, date_entree: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '8px', border: `1.5px solid ${BORDER}`, fontSize: '14px', color: NAVY, outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Valeur déclarée (€)</label>
                <input
                  type="number"
                  min="0"
                  value={addForm.valeur}
                  onChange={e => setAddForm(f => ({ ...f, valeur: e.target.value }))}
                  placeholder="0"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '8px', border: `1.5px solid ${BORDER}`, fontSize: '14px', color: NAVY, outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pièce jointe (carte grise / CPI)</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => setAddForm(f => ({ ...f, file: e.target.files[0] || null }))}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: '13px', color: '#374151', padding: '6px 0' }}
                />
                {addForm.file && <div style={{ fontSize: '12px', color: '#16a34a', marginTop: '4px' }}>📎 {addForm.file.name}</div>}
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <button
                  onClick={() => { setShowAddModal(false); setAddForm({ immat: '', date_entree: '', valeur: '', file: null }); }}
                  style={{ flex: 1, padding: '11px', borderRadius: '8px', border: `1.5px solid ${BORDER}`, background: '#fff', color: '#374151', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddVehicle}
                  disabled={parcSubmitting || !addForm.immat || !addForm.date_entree}
                  style={{ flex: 1, padding: '11px', borderRadius: '8px', border: 'none', background: (!addForm.immat || !addForm.date_entree) ? '#d1d5db' : '#16a34a', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: (!addForm.immat || !addForm.date_entree) ? 'not-allowed' : 'pointer' }}
                >
                  {parcSubmitting ? 'Envoi…' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal retirer un véhicule ── */}
      {showMdp && (
        <div onClick={fermerMdp}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ background: NAVY, borderRadius: '16px 16px 0 0', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ color: '#fff', fontSize: '16px', fontWeight: '800' }}>Changer mon mot de passe</div>
              <button onClick={fermerMdp}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '18px', lineHeight: 1, cursor: 'pointer', padding: '5px 10px' }}>✕</button>
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {mdpErreur && (
                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', fontSize: '13px', fontWeight: '600' }}>
                  {mdpErreur}
                </div>
              )}
              {[
                { cle: 'ancien', libelle: 'Mot de passe actuel', auto: 'current-password' },
                { cle: 'nouveau', libelle: 'Nouveau mot de passe', auto: 'new-password' },
                { cle: 'confirmation', libelle: 'Confirmer le nouveau mot de passe', auto: 'new-password' },
              ].map(champ => (
                <div key={champ.cle}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {champ.libelle}
                  </label>
                  <input type="password" autoComplete={champ.auto}
                    value={mdpForm[champ.cle]}
                    onChange={e => { setMdpForm(f => ({ ...f, [champ.cle]: e.target.value })); setMdpErreur(''); }}
                    onKeyDown={e => { if (e.key === 'Enter' && mdpForm.ancien && mdpForm.nouveau && mdpForm.confirmation) changerMotDePasse(); }}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '8px', border: `1.5px solid ${BORDER}`, fontSize: '14px', color: NAVY, outline: 'none' }} />
                </div>
              ))}
              {/* La règle est rappelée avant la saisie, pas découverte après un
                  refus. C'est le serveur qui la fait respecter. */}
              <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
                12 caractères minimum. Évitez les suites de chiffres, les mots courants
                et votre adresse email. Une phrase de quelques mots est plus sûre et
                plus facile à retenir qu'un mot compliqué.
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <button onClick={fermerMdp}
                  style={{ flex: 1, padding: '11px', borderRadius: '8px', border: `1.5px solid ${BORDER}`, background: '#fff', color: '#374151', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                  Annuler
                </button>
                <button onClick={changerMotDePasse}
                  disabled={mdpEnvoi || !mdpForm.ancien || !mdpForm.nouveau || !mdpForm.confirmation}
                  style={{ flex: 1, padding: '11px', borderRadius: '8px', border: 'none', background: (!mdpForm.ancien || !mdpForm.nouveau || !mdpForm.confirmation) ? '#d1d5db' : NAVY, color: '#fff', fontSize: '14px', fontWeight: '700', cursor: (!mdpForm.ancien || !mdpForm.nouveau || !mdpForm.confirmation) ? 'not-allowed' : 'pointer' }}>
                  {mdpEnvoi ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRemoveModal && (
        <div
          onClick={() => { setShowRemoveModal(false); setRemoveForm({ immat: '', date_sortie: '', file: null }); setRemoveError(''); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ background: '#dc2626', borderRadius: '16px 16px 0 0', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ color: '#fff', fontSize: '16px', fontWeight: '800' }}>➖ Retirer un véhicule</div>
              <button
                onClick={() => { setShowRemoveModal(false); setRemoveForm({ immat: '', date_sortie: '', file: null }); setRemoveError(''); }}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '18px', lineHeight: 1, cursor: 'pointer', padding: '5px 10px' }}
              >✕</button>
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {removeError && (
                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', fontSize: '13px', fontWeight: '600' }}>
                  {removeError}
                </div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Immatriculation *</label>
                <input
                  type="text"
                  value={removeForm.immat}
                  onChange={e => { setRemoveForm(f => ({ ...f, immat: formatPlaque(e.target.value) })); setRemoveError(''); }}
                  placeholder="AB-123-CD"
                  maxLength={9}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '8px', border: `1.5px solid ${removeError ? '#fca5a5' : BORDER}`, fontSize: '16px', fontFamily: 'monospace', letterSpacing: '2px', color: NAVY, outline: 'none', fontWeight: '700' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Date de sortie *</label>
                <input
                  type="date"
                  value={removeForm.date_sortie}
                  onChange={e => setRemoveForm(f => ({ ...f, date_sortie: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '8px', border: `1.5px solid ${BORDER}`, fontSize: '14px', color: NAVY, outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Certificat de cession *</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => setRemoveForm(f => ({ ...f, file: e.target.files[0] || null }))}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: '13px', color: '#374151', padding: '6px 0' }}
                />
                {removeForm.file && <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>📎 {removeForm.file.name}</div>}
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <button
                  onClick={() => { setShowRemoveModal(false); setRemoveForm({ immat: '', date_sortie: '', file: null }); setRemoveError(''); }}
                  style={{ flex: 1, padding: '11px', borderRadius: '8px', border: `1.5px solid ${BORDER}`, background: '#fff', color: '#374151', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleRemoveVehicle}
                  disabled={parcSubmitting || !removeForm.immat || !removeForm.date_sortie || !removeForm.file}
                  style={{ flex: 1, padding: '11px', borderRadius: '8px', border: 'none', background: (!removeForm.immat || !removeForm.date_sortie || !removeForm.file) ? '#d1d5db' : '#dc2626', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: (!removeForm.immat || !removeForm.date_sortie || !removeForm.file) ? 'not-allowed' : 'pointer' }}
                >
                  {parcSubmitting ? 'Envoi…' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <footer style={{ textAlign: 'center', padding: '32px 24px', borderTop: `1px solid ${BORDER}`, marginTop: '48px' }}>
        <img src={logo} alt="CB Courtage" style={{ height: '28px', opacity: 0.5, display: 'block', margin: '0 auto 10px' }} />
        <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>
          © 2026 CB Courtage — Pour toute question, contactez votre conseiller.
        </p>
      </footer>
    </div>
  );
}
